import { JwtClaims } from "@/store/modules/auth";
import * as _ from "lodash";

export enum RolesEnum {
  SysAdmin = 1,
  TenantAdmin = 2,
  AccomodationUnitAdmin = 3,
  ReceptionistLevel2 = 4,
  MaintenanceTeamMember = 5,
  StatisticsAnalyst = 6,
  ExternalWebSiteAdmin = 7,
  PropertyOwnerLevel1 = 8,
  ReceptionistLevel1 = 10,
  PropertyOwnerLevel2 = 11,
  MaintenanceTeamLeader = 12,
  PropertyManagerLevel1 = 13,
  PropertyOwnerLevel3 = 14,
  PropertyOwnerLevel1a = 15,
  PropertyOwnerLevel3a = 16
}

export function extractRoles(claims?: JwtClaims): RolesEnum[] {
  return (
    claims?.roles
      .split(",")
      .map((x: string) => +x)
      .filter((x) => _.isNumber(x)) ?? []
  );
}

export function userHasAnyRole(roles: RolesEnum[]) {
  return function (claims?: JwtClaims) {
    return (
      !_.isUndefined(claims) &&
      (roles.length === 0 ||
        extractRoles(claims).some((myRole) => roles.some((r) => myRole === r)))
    );
  };
}

export function userIsPartOfMaintenanceTeam() {
  const maintenanceTeamRoles = [RolesEnum.MaintenanceTeamMember, RolesEnum.MaintenanceTeamLeader];

  return function (claims?: JwtClaims) {
    return (
      !_.isUndefined(claims) &&
      extractRoles(claims).some((myRole) => maintenanceTeamRoles.some((r) => myRole === r))
    );
  }
}

export function userCanCreateFinancialDocuments() {
  return function(claims?: JwtClaims){
    return !_.isUndefined(claims) && policyChecker("READ_FINANCIAL_DOCUMENTS")(claims);
  }
}

export function policyChecker(policy: string): (claims?: JwtClaims) => boolean {
  switch (policy) {
    case "REQUIRE_SYS_ADMIN":
      return userHasAnyRole([RolesEnum.SysAdmin]);
    case "REQUIRE_TENANT_ADMIN_OR_HIGHER":
      return userHasAnyRole([RolesEnum.SysAdmin, RolesEnum.TenantAdmin]);
    case "REQUIRE_ACCOMODATION_UNIT_ADMIN_OR_HIGHER":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
      ]);
    case "READ_REPORTS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.StatisticsAnalyst,
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "READ_RESERVATIONS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyManagerLevel1,
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel1a,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "EXPORT_RESERVATIONS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyOwnerLevel1a,
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel3a
      ]);

    case "RESERVATION_GUEST_REMARKS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1,
      ]);
    case "RESERVATION_REMARKS_FOR_OWNER":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.PropertyOwnerLevel2,
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel1a,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "RESERVATION_PROPERTY_STAFF_REMARKS":
    case "RESERVATION_MAINTENANCE_STAFF_REMARKS":
    case "RESERVATION_UNIT_MAINTENANCE_STAFF_REMARKS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyManagerLevel1
      ]);

    case "READ_CALENDAR":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyManagerLevel1,
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel1a,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "READ_CALENDAR_RATEPLANS":
    case "READ_PAYMENTS":
    case "READ_FINANCIAL_DOCUMENTS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1,
      ]);
    case "READ_CUSTOMERS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "READ_CUSTOMERS_GRID":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1,
      ]);
    case "READ_CUSTOMER_EMAIL":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1,
      ]);
    case "MODIFY_RESERVATIONS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "READ_EVISITOR":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1
      ]);

    case "READ_MESSAGES":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyManagerLevel1,
        RolesEnum.PropertyOwnerLevel1a,
      ]);
    case "MODIFY_CALENDAR":
    case "MODIFY_CALENDAR_PRICES":
    case "MODIFY_RESERVATION_PRICES":
    case "MODIFY_RESERVATION_ADDONS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
      ]);
    case "MODIFY_CALENDAR_AVAILABILITY":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
      ]);
    case "READ_DISTRIBUTION_AND_SALES_CHANNEL":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1
      ]);
    case "SHOW_TAKEN_UNITS_ROLES":
      return userHasAnyRole([
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "ARRIVALS_KPI":
    case "DEPARTURES_KPI":
    case "CURRENT_STAYS_KPI":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.StatisticsAnalyst,
        RolesEnum.ExternalWebSiteAdmin,
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyOwnerLevel2,
        RolesEnum.PropertyManagerLevel1,
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel1a,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "RESERVATIONS_KPI":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.StatisticsAnalyst,
        RolesEnum.ExternalWebSiteAdmin,
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyOwnerLevel2,
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel1a,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "OCCUPANCY_KPI":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.StatisticsAnalyst,
        RolesEnum.ExternalWebSiteAdmin,
        RolesEnum.ReceptionistLevel1
      ]);
    case "OPTIONS_KPI":
    case "OVERBOOKING_KPI":
    case "TRANSFERS_KPI":
    case "PREPAYMENTS_KPI":
    case "BIRTHDAYS_KPI":
    case "EVISITOR_KPI":
    case "INVOICES_KPI":
    case "KPI_EXPORT":
    case "BILLING_STATUS_KPI":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1
      ]);
    case "ACTUAL_PRICES":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.StatisticsAnalyst,
        RolesEnum.ExternalWebSiteAdmin,
        RolesEnum.ReceptionistLevel1,
      ]);
    case "MODIFIED_PRICES":
      return userHasAnyRole([
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.PropertyOwnerLevel2,
      ]);
    case "INPUT_PRICES":
      return userHasAnyRole([
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel3a,
        RolesEnum.PropertyOwnerLevel1a,
      ]);
    case "MAINTENANCE_KPI":
      return userHasAnyRole([
        RolesEnum.MaintenanceTeamMember,
        RolesEnum.MaintenanceTeamLeader
      ]);
    case "GRID_TELEPHONE_NUMBER":
      return userHasAnyRole([
        RolesEnum.PropertyManagerLevel1
      ]);
    case "MODIFY_DISTRIBUTION_CHANNEL":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1
      ]);
    case "READ_PAYMENT_MEANS":
    case "READ_RESERVATION_STATUS":
    case "GLOBAL_AVAILABILITY_SEARCH":
    case "GUEST_HISTORY":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1
      ]);
    case "EDIT_RESERVATION_HOLDER_EMAIL":
      return userHasAnyRole([
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "UNIT_STATUS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.StatisticsAnalyst,
        RolesEnum.ExternalWebSiteAdmin,
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyOwnerLevel2,
        RolesEnum.PropertyManagerLevel1,
        RolesEnum.PropertyOwnerLevel1a,
      ]);
    case "READ_MEALS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.StatisticsAnalyst,
        RolesEnum.ExternalWebSiteAdmin,
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyOwnerLevel2,
        RolesEnum.PropertyManagerLevel1,
        RolesEnum.PropertyOwnerLevel1a
      ]);
    case "OWNER_LEVEL_3":
      return userHasAnyRole([
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    case "OWNER_LEVEL_1":
      return userHasAnyRole([
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.PropertyOwnerLevel1a
      ]);
    case "DASHBOARD_FILTER_OWNERS_RESERVATIONS":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.StatisticsAnalyst,
        RolesEnum.ExternalWebSiteAdmin,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyManagerLevel1,
      ]);
    case "READ_CUSTOMER_TELEPHONE_NUMBER":
      return userHasAnyRole([
        RolesEnum.SysAdmin,
        RolesEnum.TenantAdmin,
        RolesEnum.AccomodationUnitAdmin,
        RolesEnum.ReceptionistLevel2,
        RolesEnum.ReceptionistLevel1,
        RolesEnum.PropertyManagerLevel1,
        RolesEnum.PropertyOwnerLevel3,
      ]);
    case "IS_OWNER_ROLE":
      return userHasAnyRole([
        RolesEnum.PropertyOwnerLevel1,
        RolesEnum.PropertyOwnerLevel1a,
        RolesEnum.PropertyOwnerLevel2,
        RolesEnum.PropertyOwnerLevel3,
        RolesEnum.PropertyOwnerLevel3a
      ]);
    default:
      return () => false;
  }
}
